import React from 'react'
import './CancelMatchModal.css'

/**
 * CancelMatchModal - Mobile-optimized modal for confirming match cancellation
 *
 * Props:
 * - isOpen: boolean - controls modal visibility
 * - onClose: function - called when user cancels or closes modal
 * - onConfirm: function - called when user confirms cancellation
 * - matchDetails: object - optional match details to display (opponent name, date/time, opponent phone)
 * - isProcessing: boolean - shows loading state during API call
 */
export default function CancelMatchModal({
  isOpen,
  onClose,
  onConfirm,
  matchDetails = {},
  isProcessing = false,
}) {
  if (!isOpen) return null

  // Check if match is within 2 hours
  const isWithinTwoHours = () => {
    if (!matchDetails.matchDatetime) return false
    const matchTime = new Date(matchDetails.matchDatetime)
    const now = new Date()
    const twoHoursInMs = 2 * 60 * 60 * 1000
    const timeUntilMatch = matchTime - now

    // Don't allow cancellation of past matches - treat as restricted
    if (timeUntilMatch < 0) return true

    return timeUntilMatch <= twoHoursInMs
  }

  const withinTwoHours = isWithinTwoHours()

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isProcessing) {
      onClose()
    }
  }

  const handleConfirm = () => {
    if (!isProcessing && !withinTwoHours) {
      onConfirm()
    }
  }

  const handleCancel = () => {
    if (!isProcessing) {
      onClose()
    }
  }

  return (
    <div className="cancel-match-modal-overlay" onClick={handleBackdropClick}>
      <div
        className="cancel-match-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancel-match-modal-title"
      >
        <div className="modal-header">
          <h2 id="cancel-match-modal-title" className="modal-title">
            Cancel Match?
          </h2>
          {!isProcessing && (
            <button
              className="modal-close"
              onClick={onClose}
              aria-label="Close modal"
            >
              ✕
            </button>
          )}
        </div>

        <div className="modal-body">
          <div
            className="warning-icon"
            aria-label={withinTwoHours ? 'Time restriction' : 'Warning'}
          >
            {withinTwoHours ? '⏰' : '⚠️'}
          </div>

          {matchDetails.opponentName && (
            <div className="match-info">
              <p className="opponent-name">vs {matchDetails.opponentName}</p>
              {matchDetails.dateTime && (
                <p className="match-datetime">{matchDetails.dateTime}</p>
              )}
            </div>
          )}

          {withinTwoHours ? (
            <>
              <p className="warning-message">
                Cannot cancel within 2 hours of match time
              </p>
              <div className="contact-info">
                <p className="contact-instruction">
                  Please contact your opponent directly to cancel:
                </p>
                <div className="opponent-contact">
                  {matchDetails.opponentName && (
                    <p className="contact-name">
                      <strong>{matchDetails.opponentName}</strong>
                    </p>
                  )}
                  {matchDetails.opponentPhone && (
                    <p className="contact-phone">
                      <span aria-hidden="true">📱</span>{' '}
                      <a href={`tel:${matchDetails.opponentPhone}`}>
                        {matchDetails.opponentPhone}
                      </a>
                    </p>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="warning-message">
                Are you sure you want to cancel this match?
              </p>

              <div className="consequences">
                <ul className="consequences-list">
                  <li>🔄 The match will be reset to unscheduled</li>
                  <li>📧 Your opponent will be notified</li>
                  <li>� You can reschedule it later</li>
                </ul>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          {withinTwoHours ? (
            <button
              className="cancel-match-btn cancel-btn full-width"
              onClick={handleCancel}
              disabled={isProcessing}
            >
              Close
            </button>
          ) : (
            <>
              <button
                className="cancel-match-btn cancel-btn"
                onClick={handleCancel}
                disabled={isProcessing}
              >
                Keep Match
              </button>
              <button
                className="cancel-match-btn confirm-btn"
                onClick={handleConfirm}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <span className="spinner" aria-hidden="true"></span>
                    Canceling...
                  </>
                ) : (
                  'Cancel Match'
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
